<template>
  <Modal
    :show="showModal"
    size="md"
    @close="closeModal"
    headerVariant="blue"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
  >
    <template #header>
      <div class="meeting-header__text">
        <h2 class="meeting-header__title">{{ selectedMeeting?.title || "Meeting Details" }}</h2>
        <p class="meeting-header__subtitle">View meeting details</p>
      </div>
      <button type="button" class="meeting-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #default>
      <NotesModal
        v-if="showNotesModal"
        :selectedContact="selectedContact"
        @close-notes-modal="showNotesModal = false"
      />
      <EditMeetingTitleModal
        v-if="showEditMeetingModal"
        :selectedMeeting="selectedMeeting"
        @close-edit-meeting-modal="showEditMeetingModal = false"
        @refresh-meeting="refreshMeeting"
      />
      <DeleteConfirmationModal
        v-if="showDeleteConfirmationModal"
        :deleteFunction="handleDeleteMeeting"
        entity="Meeting"
        :plural="false"
        @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
        @refresh-records="handleDeleteSuccess"
      />
      <Teleport to="body">
        <CreateOpportunityModal
          v-if="showCreateOpportunityModal"
          :contactId="selectedMeeting?.contactId"
          :initialLeadSourceId="selectedMeeting?.leadSourceId"
          :showProducts="true"
          :showNotes="true"
          :showHeaderCard="true"
          @close-modal="handleCloseOpportunityModal"
          @opportunity-created="handleOpportunityCreated"
        />
      </Teleport>
      <Teleport to="body">
        <BookMeetingModal
          v-if="showRescheduleMeetingModal"
          :selectedMeeting="selectedMeeting"
          @close-book-meeting-modal="showRescheduleMeetingModal = false"
          @meeting-rescheduled="handleRescheduleMeetingSave"
        />
      </Teleport>
      <Teleport to="body">
        <AddBucketModal
          v-if="showRescheduleModal"
          :contactIds="[meetingContactId]"
          title="Reschedule"
          subtitle="Assign contact to a bucket"
          @closeAddBucketModal="showRescheduleModal = false"
          @refreshBuckets="handleRescheduleSave"
        />
      </Teleport>
      <Teleport to="body">
        <Modal
          v-if="showCongratulationsModal"
          :show="showCongratulationsModal"
          size="sm"
          @close="showCongratulationsModal = false"
          :closeOnBackdrop="false"
          :blurBackdrop="false"
          :backdropOpacity="0.5"
          :show-close-button="false"
          headerVariant="blue"
        >
          <template #header>
            <div class="meeting-header__text">
              <h2 class="meeting-header__title">Congratulations! 🎉</h2>
            </div>
            <button
              type="button"
              class="meeting-header__close"
              @click="showCongratulationsModal = false"
              aria-label="Close"
            >
              <Icon icon="mdi:close" width="18" height="18" />
            </button>
          </template>
          <div class="congrats-body">
            <div class="congrats-icon">🎉</div>
            <p class="congrats-message">
              Congratulations on having a meeting with
              <strong>{{ meetingContactFullName }}</strong
              >!
            </p>
            <p class="congrats-question">
              Do you want to create an opportunity
              <template v-if="meetingBucketName">
                and remove this contact from
                <strong>{{ meetingBucketName }}</strong> </template
              >?
            </p>
          </div>
          <template #footer>
            <div class="modal-footer-buttons">
              <Button
                type="button"
                variant="secondary"
                size="md"
                @click="handleCongratulationsNo"
                :disabled="isUpdatingStatus"
              >
                No
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                @click="handleCongratulationsYes"
                :loading="isUpdatingStatus"
                :disabled="isUpdatingStatus"
              >
                Yes
              </Button>
            </div>
          </template>
        </Modal>
      </Teleport>
      <LastInteractionModal
        v-if="showLastInteractionModal && meetingContactId"
        :contact-id="meetingContactId"
        :contact-email="selectedMeeting?.contact?.email || ''"
        @close-last-interaction-modal="showLastInteractionModal = false"
      />
      <LinkedInProfileViewerModal
        :show="showLinkedInProfileModal"
        :selectedBucket="contactLinkedInBucket"
        :linkedInChannel="linkedInChannel"
        @close="showLinkedInProfileModal = false"
      />
      <CompanyProfileViewerModal
        :show="showCompanyProfileModal"
        :selectedBucket="companyLinkedInBucket"
        :linkedInChannel="linkedInChannel"
        @close="showCompanyProfileModal = false"
      />

      <div class="modal-body-content">
        <div class="title-card">
          <div class="title-card__content">
            <div class="title-card__avatar">
              <Icon icon="mdi:video-outline" class="title-card__avatar-icon" />
            </div>
            <div class="title-card__info">
              <h5 class="title-card__name">{{ selectedMeeting.title }}</h5>
              <span class="title-card__badge">
                <Icon icon="mdi:video" width="11" height="11" />
                {{ meetingType }}
              </span>
            </div>
            <div class="title-card__actions">
              <Button
                v-if="selectedMeeting.meetingURL"
                variant="primary"
                size="sm"
                icon="mdi:video-outline"
                @click="joinMeeting"
              >
                Join Now &rarr;
              </Button>
              <PopoverMenu :items="popoverMenuItems" placement="bottom" />
            </div>
          </div>
          <div v-if="selectedMeeting.meetingURL" class="title-card__url-row">
            <Icon icon="mdi:open-in-new" class="title-card__url-icon" />
            <a
              class="title-card__url"
              :href="selectedMeeting.meetingURL"
              target="_blank"
              rel="noopener noreferrer"
              >{{ selectedMeeting.meetingURL }}</a
            >
            <button
              type="button"
              class="title-card__copy-btn"
              @click="copyToClipboard(selectedMeeting.meetingURL)"
            >
              <Icon icon="mdi:content-copy" width="14" height="14" />
              Copy
            </button>
          </div>
        </div>

        <!-- Details Section -->
        <div class="details-section">
          <div class="detail-row">
            <div class="detail-label-group">
              <span class="detail-label">Contact Name</span>
              <button
                v-if="selectedMeeting.contact?.linkedInUrl"
                type="button"
                class="linkedin-view-btn"
                @click="openLinkedInProfileModal"
                title="View LinkedIn profile"
              >
                <Icon icon="mdi:linkedin" class="linkedin-view-icon" />
                <span class="linkedin-view-text">View</span>
              </button>
            </div>
            <router-link
              :to="{
                name: CONTACT_OVERVIEW_ROUTE,
                params: { id: selectedMeeting.contact?.id },
              }"
              class="detail-value detail-value--link"
            >
              {{ capitalize(selectedMeeting.contact?.fname) }}
              {{ capitalize(selectedMeeting.contact?.lname) }}
            </router-link>
          </div>

          <div class="detail-row">
            <div class="detail-label-group">
              <span class="detail-label">Company</span>
              <button
                v-if="meetingCompanyLinkedInUrl"
                type="button"
                class="linkedin-view-btn"
                @click="openCompanyProfileModal"
                title="View LinkedIn company profile"
              >
                <Icon icon="mdi:linkedin" class="linkedin-view-icon" />
                <span class="linkedin-view-text">View</span>
              </button>
            </div>
            <router-link
              v-if="meetingCompanyId && meetingCompanyName"
              :to="{ name: COMPANY_OVERVIEW_ROUTE, params: { id: meetingCompanyId } }"
              class="detail-value detail-value--link"
            >
              {{ meetingCompanyName }}
            </router-link>
            <Icon v-else icon="mdi:minus" class="detail-value--empty" />
          </div>

          <div class="detail-row">
            <span class="detail-label">Job Title</span>
            <span v-if="meetingJobTitle" class="detail-value">{{ meetingJobTitle }}</span>
            <Icon v-else icon="mdi:minus" class="detail-value--empty" />
          </div>

          <div class="detail-row">
            <span class="detail-label">LinkedIn Link</span>
            <span v-if="selectedMeeting.contact?.linkedInUrl" class="detail-value">
              <a
                class="detail-value--link link-truncate"
                :href="selectedMeeting.contact?.linkedInUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ selectedMeeting.contact?.linkedInUrl }}
              </a>
              <Icon icon="mdi:open-in-new" class="link-icon" @click.stop />
              <Icon
                icon="mdi:content-copy"
                class="link-icon"
                @click.stop="copyToClipboard(selectedMeeting.contact?.linkedInUrl)"
              />
            </span>
            <Icon v-else icon="mdi:minus" class="detail-value--empty" />
          </div>

          <div class="detail-row">
            <span class="detail-label">Meeting Time</span>
            <span class="detail-value">
              {{ getTimeOnly(selectedMeeting.meetingDateTime) }}
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Time Zone</span>
            <span class="detail-value">{{ deviceTimeZone }} ({{ deviceTimeZoneOffset }})</span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Meeting Date</span>
            <span class="detail-value">
              {{ getDateOnly(selectedMeeting.meetingDateTime) }}
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Duration</span>
            <span v-if="selectedMeeting.duration" class="detail-value">
              {{ selectedMeeting.duration }} Minutes
            </span>
            <Icon v-else icon="mdi:minus" class="detail-value--empty" />
          </div>

          <div class="detail-row">
            <span class="detail-label">Bucket</span>
            <router-link
              v-if="meetingBucketName && meetingBucketSlug"
              :to="{
                name: BUCKETS_ROUTE,
                params: { bucket: meetingBucketSlug },
                query: { status: meetingBucketFollowUpState, contactId: meetingContactId },
              }"
              class="bucket-badge-link"
              :style="meetingBucketColorHex ? { borderColor: meetingBucketColorHex } : {}"
            >
              <span
                class="bucket-badge-link__name"
                :style="meetingBucketColorHex ? { color: meetingBucketColorHex } : {}"
              >
                {{ meetingBucketName }}
              </span>
              <span
                class="bucket-badge-link__state"
                :class="`bucket-badge-link__state--${meetingBucketFollowUpState}`"
              >
                {{
                  meetingBucketFollowUpState.charAt(0).toUpperCase() +
                  meetingBucketFollowUpState.slice(1)
                }}
              </span>
            </router-link>
            <Icon v-else icon="mdi:minus" class="detail-value--empty" />
          </div>

          <div class="detail-row">
            <span class="detail-label">Booked Date</span>
            <span class="detail-value">
              {{ getDateOnly(selectedMeeting.createdAt) }}
            </span>
          </div>

          <div
            class="guests-section"
            v-if="
              isLoadingAttendees || attendees.length > 0 || selectedMeeting.guestEmails?.length > 0
            "
          >
            <div class="guests-section__header">
              <Icon icon="mdi:account-group-outline" class="guests-section__icon" />
              <span class="guests-section__title">Guests</span>
              <span v-if="attendees.length > 0" class="guests-section__count">{{
                attendees.length
              }}</span>
            </div>
            <div v-if="isLoadingAttendees" class="guests-section__loading">
              <Icon icon="mdi:loading" class="spinner-icon" />
              <span>Loading guests...</span>
            </div>
            <div v-else-if="attendees.length > 0" class="guests-section__list">
              <div v-for="attendee in attendees" :key="attendee.id" class="guest-card">
                <img
                  v-if="guestProfilePicture(attendee.email)"
                  :src="guestProfilePicture(attendee.email)"
                  :alt="attendee.displayName || attendee.email"
                  class="guest-card__avatar-photo"
                />
                <div
                  v-else
                  class="guest-card__avatar"
                  :class="'guest-card__avatar--' + attendee.responseStatus"
                >
                  {{ (attendee.displayName || attendee.email).charAt(0).toUpperCase() }}
                </div>
                <div class="guest-card__info">
                  <span class="guest-card__name">{{ attendee.displayName || attendee.email }}</span>
                  <span v-if="attendee.displayName" class="guest-card__email">{{
                    attendee.email
                  }}</span>
                  <span class="guest-card__status-text">{{
                    rsvpStatusText(attendee.responseStatus)
                  }}</span>
                </div>
                <span :class="['rsvp-badge', rsvpBadgeClass(attendee.responseStatus)]">
                  <Icon :icon="rsvpIcon(attendee.responseStatus)" class="rsvp-badge__icon" />
                  {{ rsvpLabel(attendee.responseStatus) }}
                </span>
              </div>
            </div>
            <div v-else-if="selectedMeeting.guestEmails?.length > 0" class="guests-section__list">
              <div
                v-for="(email, index) in selectedMeeting.guestEmails"
                :key="index"
                class="guest-card"
              >
                <img
                  v-if="guestProfilePicture(email)"
                  :src="guestProfilePicture(email)"
                  :alt="email"
                  class="guest-card__avatar-photo"
                />
                <div v-else class="guest-card__avatar guest-card__avatar--needsAction">
                  {{ email.charAt(0).toUpperCase() }}
                </div>
                <div class="guest-card__info">
                  <span class="guest-card__email">{{ email }}</span>
                  <span class="guest-card__status-text">No tracking available</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="detail-row">
            <span class="detail-label">Guests</span>
            <Icon icon="mdi:minus" class="detail-value--empty" />
          </div>

          <div class="detail-row">
            <span class="detail-label">Meeting Link</span>
            <span v-if="selectedMeeting.meetingURL" class="detail-value">
              <a
                class="detail-value--link link-truncate"
                :href="selectedMeeting.meetingURL"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ selectedMeeting.meetingURL }}
              </a>
              <Icon icon="mdi:open-in-new" class="link-icon" @click.stop />
              <Icon
                icon="mdi:content-copy"
                class="link-icon"
                @click.stop="copyToClipboard(selectedMeeting.meetingURL)"
              />
            </span>
            <Icon v-else icon="mdi:minus" class="detail-value--empty" />
          </div>
        </div>

        <!-- Add Note Section -->
        <div class="note-section">
          <div class="note-section__header">
            <label class="note-section__label">Add Note</label>
            <div class="note-section__actions">
              <Button
                v-if="meetingContactId"
                variant="secondary"
                size="sm"
                icon="mdi:message-text-clock-outline"
                @click="showLastInteractionModal = true"
              >
                Last Interaction History
              </Button>
              <Button variant="secondary" size="sm" icon="mdi:history" @click="showContactNotes">
                Note History
              </Button>
            </div>
          </div>
          <textarea
            v-model="noteText"
            class="note-section__textarea"
            placeholder="Enter here"
            rows="5"
          />
          <div class="note-section__footer">
            <Button
              variant="secondary"
              size="sm"
              icon="mdi:calendar-clock"
              @click="showRescheduleMeetingModal = true"
            >
              Reschedule
            </Button>
            <Button
              variant="primary"
              size="sm"
              :loading="isRegisteringNote"
              :disabled="!noteText?.trim()"
              @click="handleRegisterNote"
            >
              Save Note
            </Button>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="modal-footer-actions">
        <Button
          variant="secondary"
          block
          :loading="
            isUpdatingStatus &&
            (updatingStatusType === MeetingStatuses.noShow ||
              (isNoShow && updatingStatusType === MeetingStatuses.pending))
          "
          :disabled="isUpdatingStatus"
          :class="['btn-no-show', { 'is-filled': isNoShow }]"
          @click="handleNoShow"
        >
          <Icon
            :icon="isNoShow ? 'mdi:undo-variant' : meetingStatusIcons[MeetingStatuses.noShow]"
            class="btn-status-icon"
          />
          {{ isNoShow ? "Undo No Show" : "No Show" }}
        </Button>
        <Button
          variant="secondary"
          block
          :loading="
            isUpdatingStatus &&
            (updatingStatusType === MeetingStatuses.meetingHeld ||
              (isMeetingHeld && updatingStatusType === MeetingStatuses.pending))
          "
          :disabled="isUpdatingStatus"
          :class="['btn-meeting-held', { 'is-filled': isMeetingHeld }]"
          @click="handleMeetingHeld"
        >
          <Icon :icon="meetingStatusIcons[MeetingStatuses.meetingHeld]" class="btn-status-icon" />
          Meeting Held
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Clipboard } from "v-clipboard";
import { Modal, Button, Icon, PopoverMenu } from "@/components/ui";
import NotesModal from "@/components/note/NotesModal.vue";
import EditMeetingTitleModal from "@/components/meeting/EditMeetingTitleModal.vue";
import BookMeetingModal from "@/components/meeting/BookMeetingModal.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import CreateOpportunityModal from "@/components/opportunity/CreateOpportunityModal.vue";
import LastInteractionModal from "@/components/bucket/LastInteractionModal.vue";
import AddBucketModal from "@/components/bucket/AddBucketModal.vue";
import LinkedInProfileViewerModal from "@/components/bucket/linkedin/LinkedInProfileViewerModal.vue";
import CompanyProfileViewerModal from "@/components/bucket/company/CompanyProfileViewerModal.vue";
import filters from "@/utils/filters";
import { CONTACT_OVERVIEW_ROUTE, BUCKETS_ROUTE, COMPANY_OVERVIEW_ROUTE } from "@/router/routes";
import { MeetingStatuses, BucketFollowUpState } from "@/utils/enums";
import { useEditMeeting, useDeleteMeeting, useMeetingAttendees } from "@/composables/meetings";
import { useDeleteBucket, useBucketOptions, useBuckets } from "@/composables/bucket";
import { useRegisterNote } from "@/composables/notes";
import { useChannelManager } from "@/composables/task-followup/useChannelManager";

// Props
const props = defineProps<{
  selectedMeeting: any;
}>();

// Emits
const emit = defineEmits<{
  closeMeetingDetailsModal: [];
  refreshMeetings: [];
}>();

const $toast = useToast();

// Store (only for account)
const store = useStore();
const account = computed(() => store.getters.account.data);

// Bucket options for name lookup
const bucketOptionsParams = computed(() =>
  account.value?.organizationId ? { organizationId: account.value.organizationId } : null
);
const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

// Contact's current bucket record (meetings API does not nest bucketOption)
const contactBucketParams = computed(() => {
  const contactId = props.selectedMeeting?.contact?.id ?? props.selectedMeeting?.contactId ?? null;
  if (!account.value?.organizationId || !contactId) return null;
  return { organizationId: account.value.organizationId, contactId };
});
const { data: contactBucketData } = useBuckets(contactBucketParams);
const existingContactBucket = computed(() => {
  const data = contactBucketData.value as any;
  return data?.data?.[0] ?? null;
});

// Mutations
const editMeetingMutation = useEditMeeting();
const deleteMeetingMutation = useDeleteMeeting();
const removeBucketMutation = useDeleteBucket();
const registerNoteMutation = useRegisterNote();

// Attendees RSVP
const meetingId = computed(() => props.selectedMeeting?.id);
const {
  attendees,
  isLoading: isLoadingAttendees,
  refetch: refetchAttendees,
} = useMeetingAttendees(meetingId);

// Guests are calendar attendees (email/displayName only) and aren't linked to a Contact
// record themselves — but the meeting's own primary contact is already loaded here, so
// a guest whose email matches that contact's email can reuse its profilePictureUrl with
// no extra request instead of falling back to the initial-letter avatar.
const meetingContact = computed(() => props.selectedMeeting?.contact ?? null);
const meetingContactEmails = computed(() => {
  const contact = meetingContact.value;
  if (!contact) return [];
  return [contact.email, ...(contact.additionalEmails || [])]
    .filter(Boolean)
    .map((email: string) => email.toLowerCase());
});

const guestProfilePicture = (email: string | undefined | null) => {
  if (!email || !meetingContact.value?.profilePictureUrl) return null;
  return meetingContactEmails.value.includes(email.toLowerCase())
    ? meetingContact.value.profilePictureUrl
    : null;
};

const rsvpBadgeClass = (status: string) => {
  switch (status) {
    case "accepted":
      return "rsvp-badge--accepted";
    case "declined":
      return "rsvp-badge--declined";
    case "tentative":
      return "rsvp-badge--tentative";
    default:
      return "rsvp-badge--awaiting";
  }
};

const rsvpLabel = (status: string) => {
  switch (status) {
    case "accepted":
      return "Accepted";
    case "declined":
      return "Declined";
    case "tentative":
      return "Tentative";
    default:
      return "Awaiting";
  }
};

const rsvpIcon = (status: string) => {
  switch (status) {
    case "accepted":
      return "mdi:check-circle-outline";
    case "declined":
      return "mdi:close-circle-outline";
    case "tentative":
      return "mdi:help-circle-outline";
    default:
      return "mdi:clock-outline";
  }
};

const rsvpStatusText = (status: string) => {
  switch (status) {
    case "accepted":
      return "Accepted the invite";
    case "declined":
      return "Declined the invite";
    case "tentative":
      return "Tentatively accepted";
    default:
      return "Hasn't responded yet";
  }
};

// LinkedIn channel
const { linkedInChannel, loadChannels } = useChannelManager($toast);

const openLinkedInProfileModal = () => {
  if (!linkedInChannel.value) {
    $toast.error("LinkedIn account not connected. Please connect it in Settings → Integrations.");
    return;
  }
  showLinkedInProfileModal.value = true;
};

const openCompanyProfileModal = () => {
  if (!linkedInChannel.value) {
    $toast.error("LinkedIn account not connected. Please connect it in Settings → Integrations.");
    return;
  }
  showCompanyProfileModal.value = true;
};

// State
const showModal = ref(true);
const showNotesModal = ref(false);
const showEditMeetingModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const showCreateOpportunityModal = ref(false);
const showLastInteractionModal = ref(false);
const showLinkedInProfileModal = ref(false);
const showCompanyProfileModal = ref(false);
const showRescheduleModal = ref(false);
const showRescheduleMeetingModal = ref(false);
const showCongratulationsModal = ref(false);
const selectedContact = ref<any>(null);
const noteText = ref("");
const isUpdatingStatus = ref(false);
const updatingStatusType = ref<string | null>(null);
const currentMeetingStatus = ref<string>(MeetingStatuses.pending);

// Computed
const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const deviceTimeZoneOffset = computed(() => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes <= 0 ? "+" : "-";
  return `GMT${sign}${String(offsetHours).padStart(2, "0")}${offsetMins ? ":" + String(offsetMins).padStart(2, "0") : ""}`;
});

const isRegisteringNote = computed(() => registerNoteMutation.isPending.value);

const normalizedStatus = (status: string | undefined) => {
  if (!status) return MeetingStatuses.pending;
  const s = (status || "").toLowerCase().replace(/_/g, "-");
  if (s === "no-show" || s === "noshow") return MeetingStatuses.noShow;
  if (s === "meeting-held" || s === "meetingheld") return MeetingStatuses.meetingHeld;
  return MeetingStatuses.pending;
};

const isNoShow = computed(() => currentMeetingStatus.value === MeetingStatuses.noShow);
const isMeetingHeld = computed(() => currentMeetingStatus.value === MeetingStatuses.meetingHeld);

const meetingStatusIcons: Record<string, string> = {
  [MeetingStatuses.noShow]: "lucide:user-x",
  [MeetingStatuses.meetingHeld]: "mdi:calendar-check",
};

const meetingContactId = computed(() => {
  const m = props.selectedMeeting;
  return m?.contact?.id ?? m?.contactId ?? "";
});

const meetingContactFullName = computed(() => {
  const contact = props.selectedMeeting?.contact;
  if (!contact) return "the contact";
  const fname = capitalize(contact.fname) || contact.fname || "";
  const lname = capitalize(contact.lname) || contact.lname || "";
  return `${fname} ${lname}`.trim() || "the contact";
});

const meetingBucketName = computed(() => {
  const bucket = existingContactBucket.value;
  if (!bucket) return null;
  const bucketOptionId = bucket.bucketOptionId;
  if (!bucketOptionId || !bucketOptionsData.value) return null;
  const option = (bucketOptionsData.value as any[]).find((o: any) => o.id === bucketOptionId);
  return option?.name ?? null;
});

const meetingBucketSlug = computed(() => {
  const bucket = existingContactBucket.value;
  if (!bucket) return null;
  const bucketOptionId = bucket.bucketOptionId;
  if (!bucketOptionId || !bucketOptionsData.value) return null;
  const option = (bucketOptionsData.value as any[]).find((o: any) => o.id === bucketOptionId);
  return option?.slug ?? null;
});

const meetingBucketColorHex = computed(() => {
  const bucket = existingContactBucket.value;
  if (!bucket?.bucketOptionId || !bucketOptionsData.value) return null;
  const option = (bucketOptionsData.value as any[]).find(
    (o: any) => o.id === bucket.bucketOptionId
  );
  return option?.colorHex ?? null;
});

const meetingBucketFollowUpState = computed((): BucketFollowUpState => {
  const bucket = existingContactBucket.value;
  if (!bucket?.followUpDate) return BucketFollowUpState.past;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const followUp = new Date(bucket.followUpDate);
  followUp.setHours(0, 0, 0, 0);
  if (followUp < today) return BucketFollowUpState.past;
  if (followUp.getTime() === today.getTime()) return BucketFollowUpState.due;
  return BucketFollowUpState.future;
});

const meetingCompanyName = computed(() => props.selectedMeeting?.contact?.company?.name ?? null);
const meetingCompanyId = computed(() => props.selectedMeeting?.contact?.company?.id ?? null);
const meetingJobTitle = computed(() => props.selectedMeeting?.contact?.jobTitle ?? null);
const meetingCompanyLinkedInUrl = computed(
  () => props.selectedMeeting?.contact?.company?.linkedInUrl ?? null
);

const contactLinkedInBucket = computed(() => ({
  contact: {
    linkedInUrl: props.selectedMeeting?.contact?.linkedInUrl,
    fname: props.selectedMeeting?.contact?.fname,
    lname: props.selectedMeeting?.contact?.lname,
    contactId: props.selectedMeeting?.contact?.id,
    profilePictureUrl: props.selectedMeeting?.contact?.profilePictureUrl ?? null,
  },
}));

const companyLinkedInBucket = computed(() => ({
  contact: {
    company: {
      linkedInUrl: props.selectedMeeting?.contact?.company?.linkedInUrl,
      name: props.selectedMeeting?.contact?.company?.name,
      companyId: props.selectedMeeting?.contact?.company?.id,
      logoUrl: props.selectedMeeting?.contact?.company?.logoUrl ?? null,
    },
  },
}));

const meetingType = computed(() => {
  const url = props.selectedMeeting?.meetingURL ?? "";
  if (url.includes("zoom.us")) return "Zoom Meeting";
  if (url.includes("meet.google.com")) return "Google Meet";
  if (url.includes("teams.microsoft.com")) return "Microsoft Teams";
  return "Video Meeting";
});

const joinMeeting = () => {
  if (props.selectedMeeting?.meetingURL) {
    window.open(props.selectedMeeting.meetingURL, "_blank", "noopener,noreferrer");
  }
};

// Filter helper functions
const capitalize = (value: string | undefined) => {
  return filters.capitalize(value ?? "") || value || "";
};

const getTimeOnly = (value: string | undefined) => {
  return filters.getTimeOnly(value ?? "") || value || "";
};

const getDateOnly = (value: string | undefined) => {
  return filters.getDateOnly(value ?? "") || value || "";
};

const popoverMenuItems = computed(() => [
  {
    label: "Edit Meeting Title",
    icon: "mdi:pencil-outline",
    onClick: () => {
      showEditMeetingModal.value = true;
    },
  },
  {
    label: "Reschedule Meeting",
    icon: "mdi:calendar-clock",
    onClick: () => {
      showRescheduleMeetingModal.value = true;
    },
  },
  {
    label: "Delete",
    icon: "mdi:trash-can-outline",
    onClick: () => {
      showDeleteConfirmationModal.value = true;
    },
  },
]);

// Methods
const closeModal = () => {
  emit("closeMeetingDetailsModal");
};

const copyToClipboard = (value: string) => {
  Clipboard.copy(value);
  $toast?.info?.("Copied to clipboard");
};

const showContactNotes = () => {
  selectedContact.value = props.selectedMeeting.contact;
  showNotesModal.value = true;
};

const refreshMeeting = (meeting: any) => {
  props.selectedMeeting.title = meeting.title;
};

const handleRescheduleMeetingSave = (meeting: any) => {
  if (meeting?.meetingDateTime) {
    props.selectedMeeting.meetingDateTime = meeting.meetingDateTime;
  }
  if (meeting?.duration) {
    props.selectedMeeting.duration = meeting.duration;
  }
  if (meeting?.meetingURL) {
    props.selectedMeeting.meetingURL = meeting.meetingURL;
  }
  showRescheduleMeetingModal.value = false;
  emit("refreshMeetings");
};

const handleNoShow = async () => {
  if (isNoShow.value) {
    await editMeetingStatus(MeetingStatuses.pending);
    return;
  }
  if (!meetingContactId.value) {
    await editMeetingStatus(MeetingStatuses.noShow);
    return;
  }
  showRescheduleModal.value = true;
};

const handleRescheduleSave = async () => {
  showRescheduleModal.value = false;
  await editMeetingStatus(MeetingStatuses.noShow);
  if (currentMeetingStatus.value === MeetingStatuses.noShow) {
    $toast?.success?.("Meeting marked as No Show");
  }
};

const handleMeetingHeld = async () => {
  if (isMeetingHeld.value) {
    await editMeetingStatus(MeetingStatuses.pending);
    return;
  }
  showCongratulationsModal.value = true;
};

const handleCongratulationsYes = async () => {
  showCongratulationsModal.value = false;
  await editMeetingStatus(MeetingStatuses.meetingHeld);
  if (currentMeetingStatus.value === MeetingStatuses.meetingHeld) {
    showCreateOpportunityModal.value = true;
  }
};

const handleCongratulationsNo = async () => {
  showCongratulationsModal.value = false;
  await editMeetingStatus(MeetingStatuses.meetingHeld);
};

const editMeetingStatus = async (status: string) => {
  isUpdatingStatus.value = true;
  updatingStatusType.value = status;

  try {
    await editMeetingMutation.mutateAsync({
      id: props.selectedMeeting.id,
      status: status,
    } as any);

    currentMeetingStatus.value = status;
    emit("refreshMeetings");
  } catch (error: any) {
    $toast?.error?.(error?.message || "Failed to update meeting status");
  } finally {
    isUpdatingStatus.value = false;
    updatingStatusType.value = null;
  }
};

const handleRegisterNote = async () => {
  if (!noteText.value?.trim()) return;

  try {
    await registerNoteMutation.mutateAsync({
      message: noteText.value,
      contactId: props.selectedMeeting.contactId,
      organizationId: account.value?.organizationId,
    });
    noteText.value = "";
  } catch {}
};

const handleDeleteMeeting = async () => {
  if (!props.selectedMeeting?.id) {
    return { success: false, message: "No meeting selected" };
  }

  try {
    await deleteMeetingMutation.mutateAsync(props.selectedMeeting.id);
    $toast?.success?.("Meeting deleted successfully");
    return true;
  } catch (error: any) {
    const errorMessage = error?.message || "Failed to delete meeting";
    $toast?.error?.(errorMessage);
    return false;
  }
};

const handleDeleteSuccess = () => {
  emit("refreshMeetings");
  closeModal();
};

const handleCloseOpportunityModal = () => {
  showCreateOpportunityModal.value = false;
};

const handleOpportunityCreated = async () => {
  const bucketId = existingContactBucket.value?.id;
  if (bucketId) {
    try {
      await removeBucketMutation.mutateAsync(bucketId);
    } catch (error: any) {
      $toast?.error?.(error?.message || "Failed to remove contact from bucket");
    }
  }
  emit("refreshMeetings");
  showCreateOpportunityModal.value = false;
  closeModal();
};

watch(
  () => props.selectedMeeting?.status,
  (status) => {
    currentMeetingStatus.value = normalizedStatus(status);
  },
  { immediate: true }
);

// Real-time RSVP updates via custom event from notification store
const onRsvpUpdated = (e: Event) => {
  const detail = (e as CustomEvent).detail;
  if (detail?.meetingId === props.selectedMeeting?.id) {
    refetchAttendees();
  }
};

onMounted(() => {
  currentMeetingStatus.value = normalizedStatus(props.selectedMeeting?.status);
  window.addEventListener("rsvp-updated", onRsvpUpdated);
  loadChannels();
});

onBeforeUnmount(() => {
  window.removeEventListener("rsvp-updated", onRsvpUpdated);
});
</script>

<style scoped>
.modal-body-content {
  padding: 24px;
  max-height: 65vh;
  overflow-y: auto;
}

.title-card {
  background-color: #f7f6fa;
  border-radius: 16px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.title-card__content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-card__avatar {
  flex-shrink: 0;
}

.title-card__avatar-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3e3efe 0%, #2d60ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 20px;
  padding: 6px;
}

.title-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title-card__name {
  font-weight: 600;
  font-size: 14px;
  margin: 0;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #3e3efe;
  background-color: #eef0ff;
  border-radius: 20px;
  padding: 2px 8px;
  width: fit-content;
}

.title-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.title-card__url-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 10px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.title-card__url-icon {
  font-size: 14px;
  color: #6b7280;
  flex-shrink: 0;
}

.title-card__url {
  flex: 1;
  font-size: 12px;
  color: #374151;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title-card__url:hover {
  color: #3e3efe;
  text-decoration: underline;
}

.title-card__copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
}

.title-card__copy-btn:hover {
  color: #3e3efe;
  background-color: #eef0ff;
}

.details-section {
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.detail-label {
  font-weight: 400;
  font-size: 12px;
  color: #707070;
}

.detail-value {
  font-weight: 400;
  font-size: 12px;
  color: #3e3efe;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-value--link {
  color: #3e3efe;
  text-decoration: none;
}

.detail-value--link:hover {
  text-decoration: underline;
}

.detail-value--empty {
  color: #9ca3af;
  font-size: 14px;
}

.link-truncate {
  max-width: 280px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.link-icon {
  font-size: 14px;
  width: 14px;
  height: 14px;
  cursor: pointer;
  color: #3e3efe;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.link-icon:hover {
  color: #2d60ff;
}

.detail-label-group {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.linkedin-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f3f1ff;
  border: 1px solid #3e3efe;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;
  color: #3e3efe;
  outline: none;
  box-shadow: none;
  flex-shrink: 0;
  white-space: nowrap;
}

.linkedin-view-btn:hover {
  background: #3e3efe;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(62, 62, 254, 0.25);
}

.linkedin-view-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(62, 62, 254, 0.2);
}

.linkedin-view-icon {
  font-size: 14px;
  transition: all 0.2s ease;
}

.linkedin-view-text {
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
}

.note-section {
  margin-top: 16px;
}

.note-section__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.note-section__label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.note-section__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.note-section__textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;
  transition: border-color 0.2s ease;
}

.note-section__textarea:focus {
  outline: none;
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.note-section__textarea::placeholder {
  color: #9ca3af;
}

.note-section__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-footer-actions {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
}

.btn-status-icon {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  flex-shrink: 0;
}

.btn-no-show {
  border-color: #ef4444 !important;
  color: #ef4444 !important;
  background-color: #ffffff !important;
}

.btn-no-show:hover:not(:disabled) {
  background-color: #fef2f2 !important;
  border-color: #dc2626 !important;
  color: #dc2626 !important;
}

.btn-no-show.is-filled {
  background-color: #ef4444 !important;
  color: #ffffff !important;
  border-color: #ef4444 !important;
}

.btn-no-show.is-filled:hover:not(:disabled) {
  background-color: #dc2626 !important;
  border-color: #dc2626 !important;
  color: #ffffff !important;
}

.btn-meeting-held {
  border-color: #22c55e !important;
  color: #22c55e !important;
  background-color: #ffffff !important;
}

.btn-meeting-held:hover:not(:disabled) {
  background-color: #f0fdf4 !important;
  border-color: #16a34a !important;
  color: #16a34a !important;
}

.btn-meeting-held.is-filled {
  background-color: #22c55e !important;
  color: #ffffff !important;
  border-color: #22c55e !important;
}

.btn-meeting-held.is-filled:hover:not(:disabled) {
  background-color: #16a34a !important;
  border-color: #16a34a !important;
  color: #ffffff !important;
}

/* Guests Section */
.guests-section {
  margin-top: 4px;
  margin-bottom: 8px;
  background-color: #f9fafb;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #f0f0f5;
}

.guests-section__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.guests-section__icon {
  font-size: 16px;
  color: #6b7280;
}

.guests-section__title {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.guests-section__count {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background-color: #e5e7eb;
  border-radius: 10px;
  padding: 1px 7px;
  margin-left: auto;
}

.guests-section__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: #9ca3af;
  font-size: 12px;
}

.guests-section__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.guest-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background-color: #ffffff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: border-color 0.15s ease;
}

.guest-card:hover {
  border-color: #d1d5db;
}

.guest-card__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background-color: #9ca3af;
}

.guest-card__avatar-photo {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.guest-card__avatar--accepted {
  background-color: #22c55e;
}

.guest-card__avatar--declined {
  background-color: #ef4444;
}

.guest-card__avatar--tentative {
  background-color: #f59e0b;
}

.guest-card__avatar--needsAction {
  background-color: #9ca3af;
}

.guest-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.guest-card__name {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.guest-card__email {
  font-size: 11px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.guest-card__status-text {
  font-size: 11px;
  color: #9ca3af;
}

.rsvp-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.rsvp-badge__icon {
  font-size: 13px;
}

.rsvp-badge--accepted {
  background-color: #dcfce7;
  color: #166534;
}

.rsvp-badge--declined {
  background-color: #fee2e2;
  color: #991b1b;
}

.rsvp-badge--tentative {
  background-color: #fef3c7;
  color: #92400e;
}

.rsvp-badge--awaiting {
  background-color: #f3f4f6;
  color: #6b7280;
}

.spinner-icon {
  animation: spin 1s linear infinite;
  font-size: 16px;
  color: #9ca3af;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.meeting-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.meeting-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}
.meeting-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}
.meeting-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.meeting-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.congrats-body {
  padding: 24px;
  text-align: center;
}

.congrats-icon {
  font-size: 48px;
  margin-bottom: 16px;
  line-height: 1;
}

.congrats-message {
  font-size: 15px;
  color: #1f2937;
  margin-bottom: 12px;
  line-height: 1.5;
}

.congrats-question {
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 0;
  line-height: 1.5;
}

.modal-footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.bucket-badge-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  border: 1.5px solid #9ca3af;
  border-radius: 6px;
  padding: 3px 10px;
  transition: opacity 0.15s ease;
}

.bucket-badge-link:hover {
  opacity: 0.75;
}

.bucket-badge-link__name {
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  white-space: nowrap;
}

.bucket-badge-link__state {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.bucket-badge-link__state--past {
  color: #ef4444;
}

.bucket-badge-link__state--due {
  color: #f59e0b;
}

.bucket-badge-link__state--future {
  color: #22c55e;
}
</style>
